import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useEffect, useState } from "react";
import {
  type NavigateFunction,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Loader from "@/components/loader";
import { PageContainer } from "@/components/page-container";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Heading } from "@/components/ui/typography";
import {
  calculateProfileCompletion,
  getIncompleteProfileFields,
} from "@/lib/profile";
import { type SystemRole, useSystemRole } from "@/lib/role-hooks";
import { useUser } from "@/lib/user-context";
import { trpc } from "@/utils/trpc";
import { AccountInformationSection } from "./profile.account";
import { EmbeddedEditProfileSection } from "./profile.edit";
import { SignInHistorySection } from "./profile.sign-in-activity";

type BasicProfileData = Record<string, unknown> & {
  fullName?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  phoneNumber?: string | null;
};

export default function Profile() {
  const { session } = useUser();
  const location = useLocation();
  const { systemRole, isLoading: isRoleLoading } = useSystemRole();
  const isAdmin = systemRole === "admin";
  const navigate = useNavigate();

  // Profile data query
  const userProfile = useQuery(trpc.getUserProfile.queryOptions());
  const profileData = (userProfile.data as BasicProfileData | null) ?? null;

  if (isRoleLoading) {
    return <Loader className="min-h-screen" />;
  }

  if (!session) {
    return null;
  }

  const isSettingsRoot = location.pathname === "/account";

  const userInitial =
    profileData?.fullName?.charAt(0) || session.user.email?.charAt(0) || "U";

  if (isAdmin) {
    return (
      <PageContainer className="pb-6">
        {isSettingsRoot ? (
          <AdminSettingsLanding
            userId={session.user.id}
            email={session.user.email}
            onNavigate={navigate}
            systemRole={systemRole}
          />
        ) : (
          <Outlet />
        )}
      </PageContainer>
    );
  }

  return (
    <PageContainer className="pb-6">
      <div className="space-y-6 lg:grid lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start lg:gap-8 lg:space-y-0">
        <aside className="hidden lg:sticky lg:top-0 lg:block lg:self-start">
          <div className="space-y-4">
            <Card>
              <CardContent className="space-y-4 pt-6 text-center">
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-primary text-3xl font-semibold text-primary-foreground">
                  {userInitial}
                </div>
                <div className="space-y-1">
                  <Heading variant="page">
                    {profileData?.fullName || "Profile"}
                  </Heading>
                  <p className="break-all text-sm text-muted-foreground">
                    {session.user.email}
                  </p>
                </div>
                {profileData ? (
                  systemRole ? (
                    <ProfileProgress profileData={profileData} />
                  ) : null
                ) : null}
              </CardContent>
            </Card>
          </div>
        </aside>

        <div className="space-y-6 lg:hidden">
          {isSettingsRoot ? (
            <>
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-primary text-3xl font-semibold text-primary-foreground">
                  {userInitial}
                </div>
                <Heading variant="page">
                  {profileData?.fullName || "Profile"}
                </Heading>
                <p className="mt-1 font-light text-muted-foreground">
                  {session.user.email}
                </p>

                {profileData ? (
                  systemRole ? (
                    <ProfileProgress profileData={profileData} />
                  ) : null
                ) : null}
              </div>
              <SettingsOverviewStack />
            </>
          ) : (
            <Outlet />
          )}
        </div>

        <main className="hidden lg:block lg:self-start">
          {isSettingsRoot ? <SettingsOverviewStack /> : <Outlet />}
        </main>
      </div>
    </PageContainer>
  );
}

function ProfileProgress({
  profileData,
}: {
  profileData: BasicProfileData | null | undefined;
}) {
  const completionData = {
    fullName: profileData?.fullName ?? null,
    company: profileData?.company ?? null,
    jobTitle: profileData?.jobTitle ?? null,
    phoneNumber: profileData?.phoneNumber ?? null,
  };
  const profilePercentage = calculateProfileCompletion(completionData);
  const incompleteFields = getIncompleteProfileFields(completionData);

  return (
    <div className="mt-8 px-4 py-2 bg-background rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium">Profile Completion</span>
        <span className="text-sm">{profilePercentage}%</span>
      </div>
      <div className="h-2 w-full rounded-full bg-primary/20">
        <div
          className="h-2 rounded-full bg-primary transition-all duration-300"
          style={{ width: `${profilePercentage}%` }}
        />
      </div>
      {incompleteFields.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Add {incompleteFields.join(", ")} to complete your profile.
        </p>
      )}
    </div>
  );
}

function SettingsOverviewStack() {
  return (
    <div className="space-y-6">
      <EmbeddedEditProfileSection />
      <AccountInformationSection />
      <SignInHistorySection />
    </div>
  );
}

function AdminSettingsLanding({
  userId,
  email,
  onNavigate,
  systemRole,
}: {
  userId?: string | null;
  email: string;
  onNavigate: NavigateFunction;
  systemRole: SystemRole;
}) {
  const resolvedUserId = userId ?? "Unavailable";

  return (
    <div className="pb-6 space-y-6 text-left">
      <PageHeader
        title="Admin settings"
        description="Review account metadata and access elevated tooling."
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Account</CardTitle>
          <CardDescription>
            Key identifiers for audit logs and support escalations.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">
              User ID
            </span>
            <CopyableValue value={resolvedUserId} />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">
              Email
            </span>
            <span className="font-medium text-foreground break-all">
              {email}
            </span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-muted-foreground">
              System role
            </span>
            <span className="inline-flex w-fit rounded-full bg-muted px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {systemRole.replace("_", " ")}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle>Security & auditing</CardTitle>
          <CardDescription>
            Jump to the tools you need to keep access tight and traceable.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <Button
            variant="outline"
            className="justify-start"
            onClick={() => onNavigate("/account/sign-in-activity")}
          >
            Manage session security
          </Button>
          <Button
            variant="outline"
            className="justify-start"
            onClick={() => onNavigate("/admin/audit-log")}
          >
            Review role audit log
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }
    const timeout = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch (error) {
      console.error("Failed to copy value", error);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="font-meta inline-flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-1.5 text-sm text-foreground transition hover:bg-muted focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
      title={copied ? "Copied to clipboard" : "Copy value"}
      aria-label={copied ? "Value copied to clipboard" : "Copy value"}
    >
      <span className="max-w-[16rem] truncate">{value}</span>
      <Copy className="h-4 w-4" aria-hidden />
      <span
        className="min-w-[3.25rem] text-right text-xs text-muted-foreground"
        aria-live="polite"
      >
        {copied ? "Copied" : "Copy"}
      </span>
    </button>
  );
}
